"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FunctionalApproval = void 0;
exports.getAttributeGlobalBoundaries = getAttributeGlobalBoundaries;
exports.getAchApprovalAttributes = getAchApprovalAttributes;
exports.getLocApprovalAttributes = getLocApprovalAttributes;
const lodash_1 = require("lodash");
const modifier_service_1 = require("../modifiers/modifier-service");
const approval_attributes_1 = require("./approval-attributes");
const approval_calculator_1 = require("./approval-calculator");
const ApprovalCalculator_utility_1 = require("./ApprovalCalculator.utility");
const payment_frequencies_1 = require("./types/payment-frequencies");
function getAttributeGlobalBoundaries(functionalApproval, minPath, maxPath) {
    const options = functionalApproval.approval.options;
    const minValues = options.map(opt => (0, lodash_1.get)(opt, minPath)), maxValues = options.map(opt => (0, lodash_1.get)(opt, maxPath));
    return {
        min: (0, lodash_1.min)(minValues),
        max: (0, lodash_1.max)(maxValues),
    };
}
function getAchApprovalAttributes() {
    return [
        Object.assign({}, approval_attributes_1.BuyRate),
        Object.assign({}, approval_attributes_1.Factor),
        Object.assign({}, approval_attributes_1.StandardAmount),
        Object.assign({}, approval_attributes_1.Term),
        Object.assign({}, approval_attributes_1.Points),
        Object.assign({}, approval_attributes_1.OriginationPercent),
        Object.assign({}, approval_attributes_1.OriginationAmount),
        Object.assign({}, approval_attributes_1.StandardPayback),
        Object.assign({}, approval_attributes_1.NumPayments),
        Object.assign({}, approval_attributes_1.StandardPaymentAmount),
        Object.assign({}, approval_attributes_1.Commission),
        Object.assign({}, approval_attributes_1.StandardDisbursement),
        Object.assign({}, approval_attributes_1.NetRequirement),
    ];
}
function getLocApprovalAttributes() {
    return [
        Object.assign({}, approval_attributes_1.Term),
        Object.assign({}, approval_attributes_1.PayoffMonths),
        Object.assign({}, approval_attributes_1.NumPayments),
        Object.assign({}, approval_attributes_1.Points),
        Object.assign({}, approval_attributes_1.PeriodicRate),
        Object.assign({}, approval_attributes_1.AnnualRate),
        Object.assign({}, approval_attributes_1.DailyRate),
        Object.assign({}, approval_attributes_1.PaymentRate),
        Object.assign({}, approval_attributes_1.EffectivePaymentCount),
        Object.assign({}, approval_attributes_1.DrawAmount),
        Object.assign({}, approval_attributes_1.LocPaymentAmount),
        Object.assign({}, approval_attributes_1.TotalInterest),
        Object.assign({}, approval_attributes_1.LocPaybackAmount),
        Object.assign({}, approval_attributes_1.DrawFee),
        Object.assign({}, approval_attributes_1.CostOfCapital),
        Object.assign({}, approval_attributes_1.CentsPerDollar),
        Object.assign({}, approval_attributes_1.Commission),
        Object.assign({}, approval_attributes_1.LocDisbursement),
        Object.assign({}, approval_attributes_1.FinalPaymentAmount),
        Object.assign({}, approval_attributes_1.AnnualPercentageRate),
    ];
}
class FunctionalApproval {
    constructor(approval, onCalculate = undefined, attributeOverrides = []) {
        this.attributesMap = {};
        this.attributeListeners = {};
        this.approval = approval;
        this.onCalculate = onCalculate;
        this.attributeOverrides = attributeOverrides;
        this.initializeCalculator();
        this.initialize();
        this.initializeAttributes();
        this.setAvailablePaymentFrequencies();
    }
    /**
     * Sets initial value for all necessary attributes
     * Sets const values for things that only need to be evaluated once and do
     * not need to be recalculated (IE, min and max term based on provided ApprovalOptions)
     */
    initialize() {
        const options = this.approval.options;
        this.availableTermAmounts = [...new Set(options.map(opt => opt.term)).values()];
        this.maxTerm = (0, lodash_1.max)(this.availableTermAmounts);
        this.maxPaymentFrequency = this.getMaxPaymentFrequency();
        this.selectedPaymentFrequency = this.maxPaymentFrequency;
        this.maxOption = this.getMaxOption();
        if (!this.maxOption) {
            throw new Error(`Unable to find max Approval option for Approval with ID: ${this.approval.id}`);
        }
        /**
         * Set Max option as initial selectedOption
         * Selected Option from this point forward will be evaluated by updateSelectedOption
         */
        this.selectedOption = this.maxOption;
    }
    initializeCalculator() {
        let config = Object.assign({}, this.approval.calculatorConfig);
        if (['term', 'loc'].includes(this.approval.calculationType)) {
            config.calculateScheduleParams = this.getPaymentScheduleParams();
        }
        this.calculator = (0, approval_calculator_1.configureCalculator)(config);
    }
    updateSelectedOption() {
        this.setValidOptions();
        this.selectedOption = this.validOptions[0];
        this.validatePaymentFrequency();
        this.calculate();
    }
    setAvailablePaymentFrequencies() {
        const term = this.selectedOption.term;
        this.availablePaymentFrequencies = this.getAvailablePaymentFrequenciesForTerm(term);
    }
    validatePaymentFrequency() {
        this.setAvailablePaymentFrequencies();
        if (!this.availablePaymentFrequencies.includes(this.selectedPaymentFrequency)) {
            this.selectedPaymentFrequency = this.availablePaymentFrequencies[0];
        }
    }
    setValidOptions() {
        const term = this.getAttributeCurrentValue('term');
        const paymentFrequency = this.selectedPaymentFrequency;
        this.validOptions = this.approval.options.filter(option => {
            return option.term === term && option.paymentFrequency === paymentFrequency.value;
        }).sort((a, b) => {
            return this.getOptionMaxAmount(b) - this.getOptionMaxAmount(b);
        });
    }
    getAttributesByCalculationType() {
        switch (this.approval.calculationType) {
            case 'standard':
            case 'maxPayment':
                return getAchApprovalAttributes();
            case 'term':
                return getLocApprovalAttributes();
            case 'loc':
                return getLocApprovalAttributes();
            case 'sba':
                return getLocApprovalAttributes();
            default:
                throw new Error(`Cannot resolve Approval Attributes for incorrect Approval calculation type: ${this.approval.calculationType}`);
        }
    }
    initializeAttributes() {
        this.setAttributesByCalculationType();
        this.attributes.forEach((att) => {
            if ('initialValueResolver' in att) {
                att.currentValue = att.initialValueResolver(this);
            }
            if ('currentBoundariesResolver' in att) {
                att.currentBoundaries = att.currentBoundariesResolver(this);
            }
            if ('globalBoundariesResolver' in att) {
                att.globalBoundaries = att.globalBoundariesResolver(this);
            }
            if ('optionsResolver' in att) {
                att.options = att.optionsResolver(this);
            }
            this.attributesMap = Object.assign(Object.assign({}, this.attributesMap), { [att.alias]: att });
            this.afterAttributeInitialized(att);
        });
        this.applyAttributeOverrides();
    }
    applyAttributeOverrides() {
        this.attributeOverrides.forEach((override) => {
            this.attributesMap[override.alias] = Object.assign(Object.assign({}, this.attributesMap[override.alias]), override);
        });
    }
    setAttributesByCalculationType() {
        this.attributes = this.getAttributesByCalculationType();
    }
    calculate() {
        this.attributes.forEach(att => {
            const alias = att.alias;
            const attribute = this.attributesMap[alias];
            // set boundaries if the attribute is Dynamic
            if ('currentBoundariesResolver' in attribute) {
                attribute.currentBoundaries = attribute.currentBoundariesResolver(this);
            }
            if ('globalBoundariesResolver' in attribute) {
                attribute.globalBoundaries = attribute.globalBoundariesResolver(this);
            }
            if ('currentValueResolver' in attribute) {
                const newValue = attribute.currentValueResolver(this);
                if (newValue !== attribute.currentValue) {
                    attribute.currentValue = newValue;
                    (0, lodash_1.isFunction)(this.attributeListeners[alias]) && this.attributeListeners[alias]();
                }
            }
            if ('optionsResolver' in attribute) {
                attribute.options = attribute.optionsResolver(this);
            }
            this.validateAttributeValue(attribute);
            this.attributesMap[alias] = Object.assign({}, attribute);
        });
        this.applyModifiers();
        (0, lodash_1.isFunction)(this.onCalculate) && this.onCalculate();
    }
    validateAttributeValue(attribute) {
        var _a, _b, _c, _d;
        const val = attribute.currentValue;
        const min = ((_a = attribute.currentBoundaries) === null || _a === void 0 ? void 0 : _a.min) || ((_b = attribute.globalBoundaries) === null || _b === void 0 ? void 0 : _b.min);
        const max = ((_c = attribute.currentBoundaries) === null || _c === void 0 ? void 0 : _c.max) || ((_d = attribute.globalBoundaries) === null || _d === void 0 ? void 0 : _d.max);
        if (val < min) {
            attribute.currentValue = min;
        }
        if (val > max) {
            attribute.currentValue = max;
        }
    }
    getMaxPaymentFrequency() {
        return this.approval.options
            .map(opt => payment_frequencies_1.FREQ_MAP[opt.paymentFrequency])
            .sort((a, b) => {
            return b.numDays - a.numDays;
        })[0];
    }
    getAvailablePaymentFrequenciesForTerm(term) {
        const { options } = this.approval;
        const validOptionsFrequencyValues = options.filter(opt => opt.term === term)
            .map(opt => opt.paymentFrequency);
        //@ts-ignore
        const optionFrequencies = [...new Set(validOptionsFrequencyValues).values()];
        return optionFrequencies.map(freq => payment_frequencies_1.FREQ_MAP[freq])
            .sort((a, b) => {
            return b.numDays - a.numDays;
        });
    }
    getMaxOption() {
        return this.approval.options.filter(o => o.term === this.maxTerm && o.paymentFrequency === this.maxPaymentFrequency.value)
            .sort((a, b) => {
            return this.getOptionMaxAmount(b) - this.getOptionMaxAmount(b);
        })[0];
    }
    getOptionMaxAmount(option) {
        var _a;
        const amountLimitKey = this.approval.calculationType === 'loc' ? 'drawAmount' : 'amount';
        return ((_a = (0, lodash_1.get)(option.limits, amountLimitKey)) === null || _a === void 0 ? void 0 : _a.max) || 0;
    }
    setCurrentOptionOriginationParams() {
        if (['standard', 'maxPayment'].includes(this.approval.calculationType)) {
            const option = this.selectedOption;
            const amount = this.getAttributeCurrentValue('amount');
            this.originationParams = (0, ApprovalCalculator_utility_1.getOriginationParams)(option.limits, amount);
        }
    }
    getAttributeInitialValue(attributeAlias) {
        const attribute = this.attributesMap[attributeAlias];
        return !!attribute ? attribute.initialValueResolver(this) : null;
    }
    getAttributeCurrentValue(attributeAlias) {
        const attribute = this.attributesMap[attributeAlias];
        return attribute === null || attribute === void 0 ? void 0 : attribute.currentValue;
    }
    setPaymentFrequencyFromStringValue(frequencyValue) {
        this.selectedPaymentFrequency = payment_frequencies_1.FREQ_MAP[frequencyValue];
        this.updateSelectedOption();
    }
    setAttributeValue(attributeAlias, value, calculate = false) {
        const attribute = this.attributesMap[attributeAlias];
        if (!attribute || attribute.currentValue === value) {
            return;
        }
        attribute.currentValue = value;
        if (attributeAlias === 'term') {
            this.updateSelectedOption();
        }
        (0, lodash_1.isFunction)(this.attributeListeners[attributeAlias]) && this.attributeListeners[attributeAlias]();
        if (calculate) {
            this.calculate();
        }
    }
    registerAttributeListener(attributeAlias, listener) {
        if ((0, lodash_1.isFunction)(listener) && this.attributesMap.hasOwnProperty(attributeAlias)) {
            this.attributeListeners[attributeAlias] = listener;
        }
    }
    getPaymentScheduleParams() {
        return {
            paymentCount: this.getAttributeCurrentValue('effectivePaymentCount'),
            paymentRate: this.getAttributeCurrentValue('paymentRate'),
            paymentAmount: this.getAttributeCurrentValue('paymentAmount'),
            startBalance: this.getAttributeCurrentValue('drawAmount'),
        };
    }
    afterAttributeInitialized(attribute) {
        if (attribute.alias === 'paymentAmount') {
            this.initializeCalculator();
        }
    }
    applyModifiers() {
        if (this.approval.modifiers) {
            (new modifier_service_1.ModifierService).applyModifiers(this.approval.modifiers, this);
        }
    }
}
exports.FunctionalApproval = FunctionalApproval;
